import { Trans, useLingui } from "@lingui/react/macro";
import {
  CONSENT_VERSION,
  dispatchConsentEvent,
  getStoredConsent,
  setConsentCookie,
  type CookieConsent,
} from "@pontimed/analytics";
import { apiFetch } from "@pontimed/api-client";
import { useNavigate } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import { useCallback, useEffect, useId, useState } from "react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/**
 * Window event that reopens the banner with the stored choices pre-filled —
 * the same name apps/web uses, so both apps share one convention.
 */
export const COOKIE_CONSENT_REOPEN_EVENT = "cookie-consent-reopen";

/**
 * Reopen the cookie settings. Withdrawing consent must be as easy as giving
 * it (GDPR art. 7(3)); before this the portal showed the banner once and had
 * no way back to it.
 */
export function openCookieSettings(): void {
  window.dispatchEvent(new Event(COOKIE_CONSENT_REOPEN_EVENT));
}

/**
 * Cookie consent banner for the portal SPA. Port of legacy
 * `src/components/layout/cookie-consent.tsx` adapted to Lingui i18n + TanStack
 * Router (legacy uses next-intl + Next.js Link/useParams).
 *
 * Lifecycle:
 *  - Mount: read consent cookie. If unset (no choice ever) or version
 *    mismatch (policy bumped), schedule the banner to appear after 800ms.
 *  - User picks accept-all / reject-optional / save-preferences. Cookie
 *    written + custom event broadcast (PostHogProvider + GA gtag listen).
 *  - `openCookieSettings()` (footer, side nav, settings) reopens it with the
 *    stored choices and the details expanded.
 *  - Withdrawing "marketing" (granted → not granted) also tells the API, which
 *    clears the signed-in person's stored ad click ids and ads consent so no
 *    queued conversion is uploaded afterwards. Anonymous visitors get a 401;
 *    there is nothing server-side to withdraw for them.
 *
 * Cookie domain: omitted in dev (host-only), set to `.pontimed.app` in prod
 * via the `VITE_COOKIE_DOMAIN` env var so consent persists across web /
 * portal / admin subdomains.
 */
export function CookieConsentBanner() {
  const { t } = useLingui();
  const navigate = useNavigate();

  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  // Each consent Switch is named by its title and described by its line
  // (P26) — a screen reader otherwise hears "switch, off" four times.
  const idBase = useId();
  const ids = {
    title: `${idBase}-title`,
    necessary: `${idBase}-necessary`,
    functional: `${idBase}-functional`,
    analytics: `${idBase}-analytics`,
    marketing: `${idBase}-marketing`,
  };
  // Default OFF (hard opt-in per ePrivacy + CNIL/EDPB guidance).
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      // Small delay so the page paints first.
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    function handleReopen() {
      const existing = getStoredConsent();
      if (existing) {
        setFunctional(existing.functional);
        setAnalytics(existing.analytics);
        setMarketing(existing.marketing);
      }
      setShowDetails(true);
      setVisible(true);
    }
    window.addEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
    return () => window.removeEventListener(COOKIE_CONSENT_REOPEN_EVENT, handleReopen);
  }, []);

  const cookieDomain =
    import.meta.env.VITE_COOKIE_DOMAIN ?? import.meta.env.NEXT_PUBLIC_COOKIE_DOMAIN ?? undefined;

  const saveConsent = useCallback(
    (consent: CookieConsent) => {
      const previous = getStoredConsent();
      setConsentCookie(consent, { domain: cookieDomain });
      dispatchConsentEvent(consent);
      setVisible(false);
      // Only a real withdrawal (granted before, not now) — never on a first
      // choice or on re-saving a refusal. Best-effort: the cookie is already
      // updated and client-side attribution follows the consent event.
      if (previous?.marketing === true && !consent.marketing) {
        void apiFetch.post("/api/account/ads-consent/withdraw").catch(() => {});
      }
    },
    [cookieDomain],
  );

  const acceptAll = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  }, [saveConsent]);

  const rejectOptional = useCallback(() => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  }, [saveConsent]);

  const savePreferences = useCallback(() => {
    saveConsent({
      necessary: true,
      functional,
      analytics,
      marketing,
      timestamp: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  }, [saveConsent, functional, analytics, marketing]);

  const openCookiesPolicy = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      // LegalSheetProvider listens to ?legal=cookies and opens the in-app
      // legal sheet so the user never leaves the portal shell.
      navigate({ to: ".", search: (prev) => ({ ...prev, legal: "cookies" }) });
    },
    [navigate],
  );

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
          className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
        >
          <div
            role="region"
            aria-labelledby={ids.title}
            className="bg-card border-border mx-auto max-w-lg rounded-xl border shadow-lg"
          >
            <div className="p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="bg-primary-soft flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-primary"
                    aria-hidden
                  >
                    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                    <path d="M8.5 8.5v.01" />
                    <path d="M16 15.5v.01" />
                    <path d="M12 12v.01" />
                    <path d="M11 17v.01" />
                    <path d="M7 14v.01" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <h3 id={ids.title} className="text-foreground text-sm font-semibold">
                    <Trans>We respect your privacy</Trans>
                  </h3>
                  <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                    <Trans>
                      We use cookies to keep PontiMed working, remember your preferences, and
                      measure how the platform is used.
                    </Trans>{" "}
                    <a
                      href="?legal=cookies"
                      onClick={openCookiesPolicy}
                      className="text-primary hover:underline"
                    >
                      <Trans>Learn more</Trans>
                    </a>
                  </p>
                </div>
              </div>

              <AnimatePresence>
                {showDetails && (
                  <m.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p
                            id={`${ids.necessary}-label`}
                            className="text-foreground text-xs font-medium"
                          >
                            <Trans>Strictly necessary</Trans>
                          </p>
                          <p id={`${ids.necessary}-desc`} className="text-muted-foreground text-xs">
                            <Trans>Authentication, security, language preference.</Trans>
                          </p>
                        </div>
                        <Switch
                          checked
                          disabled
                          className="opacity-60"
                          aria-labelledby={`${ids.necessary}-label`}
                          aria-describedby={`${ids.necessary}-desc`}
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p
                            id={`${ids.functional}-label`}
                            className="text-foreground text-xs font-medium"
                          >
                            <Trans>Functional</Trans>
                          </p>
                          <p
                            id={`${ids.functional}-desc`}
                            className="text-muted-foreground text-xs"
                          >
                            <Trans>Remembers your country, theme, and recent searches.</Trans>
                          </p>
                        </div>
                        <Switch
                          checked={functional}
                          onCheckedChange={setFunctional}
                          aria-labelledby={`${ids.functional}-label`}
                          aria-describedby={`${ids.functional}-desc`}
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p
                            id={`${ids.analytics}-label`}
                            className="text-foreground text-xs font-medium"
                          >
                            <Trans>Analytics</Trans>
                          </p>
                          <p id={`${ids.analytics}-desc`} className="text-muted-foreground text-xs">
                            <Trans>
                              Helps us understand which features matter so we can improve PontiMed.
                            </Trans>
                          </p>
                        </div>
                        <Switch
                          checked={analytics}
                          onCheckedChange={setAnalytics}
                          aria-labelledby={`${ids.analytics}-label`}
                          aria-describedby={`${ids.analytics}-desc`}
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p
                            id={`${ids.marketing}-label`}
                            className="text-foreground text-xs font-medium"
                          >
                            <Trans>Marketing</Trans>
                          </p>
                          <p id={`${ids.marketing}-desc`} className="text-muted-foreground text-xs">
                            <Trans>Personalised content and promotional offers.</Trans>
                          </p>
                        </div>
                        <Switch
                          checked={marketing}
                          onCheckedChange={setMarketing}
                          aria-labelledby={`${ids.marketing}-label`}
                          aria-describedby={`${ids.marketing}-desc`}
                        />
                      </div>
                    </div>
                  </m.div>
                )}
              </AnimatePresence>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={() => setShowDetails((d) => !d)}
                  className="text-muted-foreground hover:text-foreground inline-flex min-h-11 items-center self-start text-xs underline underline-offset-2 transition-colors"
                >
                  {showDetails ? t`Hide details` : t`Show details`}
                </button>
                <div className="flex gap-2">
                  {showDetails ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-11 flex-1 sm:flex-none"
                        onClick={rejectOptional}
                      >
                        <Trans>Reject optional</Trans>
                      </Button>
                      <Button
                        size="sm"
                        className="min-h-11 flex-1 sm:flex-none"
                        onClick={savePreferences}
                      >
                        <Trans>Save preferences</Trans>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-h-11 flex-1 sm:flex-none"
                        onClick={rejectOptional}
                      >
                        <Trans>Reject optional</Trans>
                      </Button>
                      <Button
                        size="sm"
                        className="min-h-11 flex-1 sm:flex-none"
                        onClick={acceptAll}
                      >
                        <Trans>Accept all</Trans>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
